import i18nComponentsXpsInstance from '@cohort/components-xps/lib/i18n';
import {formatI18nLanguage} from '@cohort/shared/utils/localization';
import {useUserStore} from '@cohort/wallet/hooks/stores/user';
import {useMerchantContext} from '@cohort/wallet/hooks/useMerchantContext';
import {initI18n} from '@cohort/wallet/lib/i18n';
import {useQueryClient} from '@tanstack/react-query';
import dayjs from 'dayjs';
import {changeLanguage} from 'i18next';
import {useCallback, useEffect, useRef} from 'react';
import {getI18n} from 'react-i18next';
import {useSearchParams} from 'react-router-dom';

const DEFAULT_LANGUAGE = 'en';

export const useLocalization = (): void => {
  const user = useUserStore(store => store.user);
  const merchant = useMerchantContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialized = useRef(false);
  const queryClient = useQueryClient();

  const setLanguage = useCallback(
    (language: string): void => {
      if (language !== getI18n().language || language !== i18nComponentsXpsInstance.language) {
        dayjs.locale(language);
        changeLanguage(language);
        i18nComponentsXpsInstance.changeLanguage(language);
        queryClient.invalidateQueries();
        localStorage.setItem('lng-wallet', language);
      }
    },
    [queryClient]
  );

  useEffect(() => {
    // fallback language should be merchant default only if english is not supported
    if (!merchant.supportedLanguages.some(lang => lang === DEFAULT_LANGUAGE)) {
      initI18n({fallbackLng: merchant.defaultLanguage});
      i18nComponentsXpsInstance.init({fallbackLng: merchant.defaultLanguage});
      i18nComponentsXpsInstance.changeLanguage(merchant.defaultLanguage);
    }
  }, [merchant]);

  useEffect(() => {
    const initialize = (): void => {
      initialized.current = true;
    };

    const langParam = searchParams.get('lng');
    if (langParam !== null) {
      searchParams.delete('lng');
      setSearchParams(searchParams, {replace: true});
    }

    // priority 0: user logged in
    if (user) {
      setLanguage(user.lang);
      return initialize;
    }

    if (initialized.current) {
      return;
    }

    // priority 1: lng param
    if (langParam && merchant.supportedLanguages.some(lang => lang === langParam)) {
      setLanguage(langParam);
      return initialize;
    }

    // priority 2: storage
    const storageLang = localStorage.getItem('lng-wallet');
    if (storageLang !== null && merchant.supportedLanguages.some(lang => lang === storageLang)) {
      setLanguage(storageLang);
      return initialize;
    }

    // priority 3: navigator settings
    const browserLanguage = formatI18nLanguage(navigator.language);
    if (merchant.supportedLanguages.some(lang => lang === browserLanguage)) {
      setLanguage(browserLanguage);
      return initialize;
    }

    // priority 4: english
    if (merchant.supportedLanguages.some(lang => lang === DEFAULT_LANGUAGE)) {
      setLanguage(DEFAULT_LANGUAGE);
      return initialize;
    }

    // priority 5: merchant default
    setLanguage(merchant.defaultLanguage);
    return initialize;
  }, [merchant, searchParams, setLanguage, setSearchParams, user]);
};
